import { GraphQLFieldConfig, GraphQLNonNull } from 'graphql'
import { VulnerabilityOrder } from '../types/vulnerability-order'
import { Context } from '../shared'
import {
  vulnerabilityPagingArgs,
  VulnerabilityPagingArgs
} from '../types/vulnerability-paging-args'
import { VulnerabilityOrderField } from '../types/vulnerability-order-field'
import {
  ContrastQueryParameters,
  appendVulnerabilityFiltersToBody
} from '../endpoint-helpers'
import {
  VulnerabilityGridPage,
  vulnerabilityGridPageType
} from '../types/vulnerability-grid-page'
import { normalizeVulnerabilitySinkGroups } from './helpers/vulnerabilities-and-sink-groups'

export const unGroupedSortValues: { [key in VulnerabilityOrderField]: string } =
  {
    APPLICATION: 'application.name',
    FIRST_DETECTED: 'firstTimeSeen',
    LAST_DETECTED: 'lastTimeSeen',
    NAME: 'title',
    STATUS: 'status',
    SEVERITY: 'severity'
  }

export const groupedSortValues: Partial<{
  [key in VulnerabilityOrderField]: string
}> = {
  FIRST_DETECTED: 'firstTimeSeen',
  LAST_DETECTED: 'lastTimeSeen',
  SEVERITY: 'severity'
}

export function appendSortToParams(
  params: ContrastQueryParameters,
  orderBy: VulnerabilityOrder | undefined,
  sortValues: Partial<{ [key in VulnerabilityOrderField]: string }>
) {
  if (orderBy) {
    const { field, direction } = orderBy
    params.append(
      'sort',
      `${direction === 'DESC' ? '-' : ''}${sortValues[field]}`
    )
  }
}

const expandParams = 'application'

function createParams(offset, limit, orderBy, filters) {
  const params = new ContrastQueryParameters()

  let expand = expandParams
  let isGroupedRequest = false

  if (filters) {
    expand = Array.isArray(filters.sessionMetadata)
      ? expandParams + ',session_metadata'
      : expandParams

    if (filters.groupedBy) {
      isGroupedRequest = true
    }
  }

  params.append('expand', expand)
  params.append('offset', offset)
  params.append('limit', limit)
  appendSortToParams(
    params,
    orderBy,
    isGroupedRequest ? groupedSortValues : unGroupedSortValues
  )

  return params
}

export const vulnerabilitiesAndSinkGroups: GraphQLFieldConfig<
  any,
  Context,
  VulnerabilityPagingArgs
> = {
  description: 'Get a page of vulnerabilities and sink groups',
  type: new GraphQLNonNull(vulnerabilityGridPageType),
  args: vulnerabilityPagingArgs,
  async resolve(
    _source,
    args,
    context
  ): Promise<Partial<VulnerabilityGridPage>> {
    const { offset, limit, organizationID, orderBy, filters } = args

    const params = createParams(offset, limit, orderBy, filters)
    const mappedFilters = appendVulnerabilityFiltersToBody(filters)

    const {
      items,
      count: total,
      licensedVulnerabilityCount
    } = await context.connector.post(
      `/organizations/${organizationID}/orgtraces/ui?${params}`,
      mappedFilters
    )

    return {
      total,
      licensedVulnerabilityCount,
      items: normalizeVulnerabilitySinkGroups(items)
    }
  }
}
